import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';
import { ApiError } from '@/api/client';

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = (loc.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      nav(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Account temporarily locked — too many attempts. Try again shortly.');
      } else if (err instanceof ApiError && err.status === 401) {
        setError('Invalid email or password.');
      } else {
        setError(err instanceof Error ? err.message : 'Login failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        zIndex: 1,
        padding: 24,
      }}
    >
      <div className="fade-up" style={{ width: 380, maxWidth: '100%' }}>
        <div style={{ marginBottom: 22 }}>
          <div className="eyebrow">WiFi Traffic-as-a-Service</div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 34,
              fontWeight: 700,
              letterSpacing: '0.02em',
              lineHeight: 1.05,
              marginTop: 6,
            }}
          >
            <span className="signal">Z</span>EPHLON
          </div>
          <div className="dim mono" style={{ fontSize: 12, marginTop: 8 }}>
            control console — operator sign-in
          </div>
        </div>

        <form className="card" onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
          <div className="field">
            <label className="label" htmlFor="email">Email</label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="password">Password</label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div className="mono" style={{ color: 'var(--loss)', fontSize: 13 }}>
              ▸ {error}
            </div>
          )}

          <button
            className="btn btn-primary"
            type="submit"
            disabled={busy}
            style={{ justifyContent: 'center' }}
          >
            {busy ? <span className="spinner" /> : 'Authenticate →'}
          </button>
        </form>

        <div className="muted mono" style={{ fontSize: 11, marginTop: 16, textAlign: 'center' }}>
          accounts are provisioned by an administrator
        </div>
        <div className="muted mono" style={{ fontSize: 11, marginTop: 8, textAlign: 'center' }}>
          access token held in memory · refresh via secure cookie
        </div>
      </div>
    </div>
  );
}
